import React, { useState, useMemo, useEffect } from "react";
import {
  Theme,
  TextField,
  Button,
  Card,
  Flex,
  Tooltip,
  Badge,
  DropdownMenu,
  IconButton,
  Dialog,
} from "@radix-ui/themes";
import Markdown, { type Components } from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceDot,
} from "recharts";
import "./index.css";

interface Citation {
  source?: string;
  url?: string;
  title?: string;
  document_title?: string;
  cited_text?: string;
  citedDates?: string[];
}

type Tool = {
  id?: string;
  name: string;
  input: Record<string, unknown>;
  error?: string;
};
type Segment =
  | { type: "text"; content: string }
  | { type: "tools"; tools: Tool[] }
  | { type: "thinking"; content: string; done: boolean }
  | {
      type: "chart";
      url: string;
      title: string;
      units: string;
      data: { date: string; value: string }[];
    };

const toolConfig: Record<
  string,
  {
    color: "blue" | "amber" | "green" | "gray";
    label: string;
    primaryParam: string;
    quote?: boolean;
  }
> = {
  fred_search: {
    color: "blue",
    label: "FRED Search",
    primaryParam: "query",
    quote: true,
  },
  fred_get_series: {
    color: "blue",
    label: "FRED Series",
    primaryParam: "series_id",
  },
  bls_get_series: {
    color: "green",
    label: "BLS Series",
    primaryParam: "series_id",
  },
  web_search: {
    color: "amber",
    label: "Web Search",
    primaryParam: "query",
    quote: true,
  },
};

function ToolChip({ tool, isRunning }: { tool: Tool; isRunning: boolean }) {
  const config = toolConfig[tool.name] || {
    color: "gray",
    label: tool.name,
    primaryParam: "",
  };
  const color = tool.error ? "red" : config.color;
  const primaryValue = config.primaryParam
    ? tool.input[config.primaryParam]
    : null;
  const formattedValue = primaryValue
    ? config.quote
      ? `"${primaryValue}"`
      : primaryValue
    : null;
  const label = formattedValue
    ? `${config.label}: ${formattedValue}`
    : config.label;
  const tooltip = tool.error
    ? `Error: ${tool.error}`
    : Object.entries(tool.input)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ") || "no params";
  return (
    <Tooltip content={tooltip}>
      <Badge
        color={color}
        variant="soft"
        className={`cursor-help ${isRunning ? "opacity-60" : ""}`}
        style={{
          maxWidth: "12rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "inline-block",
          verticalAlign: "middle",
        }}
      >
        {label}
        {tool.error ? " ✗" : ""}
      </Badge>
    </Tooltip>
  );
}

function TypingIndicator() {
  return (
    <span className="inline-flex gap-1 py-2">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 bg-faint rounded-full animate-bounce"
          style={{ animationDelay: `${i * 0.2}s` }}
        />
      ))}
    </span>
  );
}

interface ChartDataPoint {
  date: string;
  value: number | null;
  highlighted: boolean;
}

function SourceChart({
  data,
  highlightedDates,
  title,
  units,
}: {
  data: unknown[];
  highlightedDates: string[];
  title: string;
  units: string;
}) {
  const [expanded, setExpanded] = useState(false);

  // Handle FRED-style observations: [{date, value}, ...]
  const observations = data.filter(
    (d): d is { date: string; value: string } =>
      typeof d === "object" && d !== null && "date" in d && "value" in d,
  );

  const chartData: ChartDataPoint[] = useMemo(() => {
    return observations
      .map((o) => {
        const parsed = parseFloat(o.value);
        return {
          date: o.date,
          value: o.value === "." || isNaN(parsed) ? null : parsed,
          highlighted: highlightedDates.includes(o.date),
        };
      })
      .reverse(); // Chronological order
  }, [observations, highlightedDates]);

  // Don't render if no valid data points
  if (chartData.length === 0 || chartData.every((d) => d.value === null)) {
    return null;
  }

  // Build contiguous ranges from highlighted dates for ReferenceArea
  const highlightRanges = useMemo(() => {
    const highlighted = chartData.filter((d) => d.highlighted);
    if (highlighted.length === 0) return [];

    // Find indices of highlighted points in chartData
    const indices = highlighted
      .map((h) => chartData.findIndex((d) => d.date === h.date))
      .filter((i) => i >= 0)
      .sort((a, b) => a - b);

    if (indices.length === 0) return [];

    // Group into contiguous ranges
    const ranges: { start: string; end: string }[] = [];
    let rangeStart = indices[0]!;
    let rangeEnd = indices[0]!;

    for (let i = 1; i < indices.length; i++) {
      const idx = indices[i]!;
      if (idx === rangeEnd + 1) {
        rangeEnd = idx;
      } else {
        ranges.push({
          start: chartData[rangeStart]!.date,
          end: chartData[rangeEnd]!.date,
        });
        rangeStart = idx;
        rangeEnd = idx;
      }
    }
    ranges.push({
      start: chartData[rangeStart]!.date,
      end: chartData[rangeEnd]!.date,
    });

    return ranges;
  }, [chartData]);

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full h-64 cursor-pointer hover:opacity-90"
      >
        <ResponsiveContainer width="100%" height={256}>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 5, bottom: 5, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-6)" />
            <XAxis
              dataKey="date"
              tickFormatter={(d) => d.slice(0, 7)}
              tick={{ fontSize: 12, fill: "var(--gray-11)" }}
              tickLine={false}
              axisLine={{ stroke: "var(--gray-6)" }}
            />
            <YAxis
              domain={["auto", "auto"]}
              tick={{ fontSize: 12, fill: "var(--gray-11)" }}
              tickLine={false}
              axisLine={{ stroke: "var(--gray-6)" }}
              label={
                units
                  ? {
                      value: units,
                      angle: -90,
                      position: "insideLeft",
                      style: {
                        fontSize: 11,
                        fill: "var(--gray-11)",
                        textAnchor: "middle",
                      },
                    }
                  : undefined
              }
            />
            <RechartsTooltip
              contentStyle={{
                background: "var(--gray-1)",
                border: "1px solid var(--gray-6)",
                fontSize: 12,
              }}
              formatter={(value) => [
                value != null ? value.toLocaleString() : "",
                title,
              ]}
              labelFormatter={(label) => label}
            />
            {highlightRanges.map((range, i) => {
              const value = chartData.find((d) => d.date === range.start)?.value;
              if (range.start === range.end) {
                if (value == null) return null;
                return (
                  <ReferenceDot
                    key={i}
                    x={range.start}
                    y={value}
                    r={5}
                    fill="var(--red-9)"
                    fillOpacity={0.7}
                    stroke="none"
                  />
                );
              }
              return (
                <ReferenceArea
                  key={i}
                  x1={range.start}
                  x2={range.end}
                  fill="var(--red-9)"
                  fillOpacity={0.3}
                  strokeOpacity={0}
                />
              );
            })}
            <Line
              type="monotone"
              dataKey="value"
              stroke="var(--chart-line)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: "var(--chart-line)" }}
              isAnimationActive={false}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </button>
      {expanded && (
        <div className="mt-2 text-xs font-mono p-2 max-h-48 overflow-y-auto border border-subtle">
          {observations.slice(0, 20).map((obs, i) => (
            <div
              key={i}
              className="flex gap-4"
              style={
                highlightedDates.includes(obs.date)
                  ? { color: "var(--red-11)", fontWeight: 600 }
                  : undefined
              }
            >
              <span className="text-muted">{obs.date}</span>
              <span>{obs.value}</span>
            </div>
          ))}
          {observations.length > 20 && (
            <div className="text-muted mt-1">
              ...and {observations.length - 20} more
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ThinkingBlock({ content, done }: { content: string; done: boolean }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="my-3 overflow-hidden"
      style={{ backgroundColor: "var(--gray-a2)" }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2 text-left text-xs flex items-center gap-2"
      >
        <span
          className={`text-faint transition-transform ${expanded ? "rotate-90" : ""}`}
        >
          ▶︎
        </span>
        <span className={`text-tertiary ${done ? "" : "animate-pulse"}`}>
          {done ? "Reasoning" : "Thinking..."}
        </span>
      </button>
      {expanded && (
        <div
          className="px-3 py-2 text-xs text-secondary whitespace-pre-wrap max-h-64 overflow-y-auto"
          style={{ borderTop: "1px solid var(--gray-a6)" }}
        >
          {content || (
            <span className="text-faint italic">Starting to think...</span>
          )}
        </div>
      )}
    </div>
  );
}

export function App() {
  const [query, setQuery] = useState("");
  const [segments, setSegments] = useState<Segment[]>([]);
  const [sources, setSources] = useState<
    { index: number; citation: Citation }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [hoveredFootnote, setHoveredFootnote] = useState<number | null>(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [theme, setTheme] = useState<"light" | "dark" | "system">(() => {
    const saved = localStorage.getItem("theme");
    if (saved === "light" || saved === "dark") return saved;
    return "system";
  });
  const [systemPreference, setSystemPreference] = useState<"light" | "dark">(
    () =>
      window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light",
  );

  useEffect(() => {
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) =>
      setSystemPreference(e.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  const resolvedTheme = theme === "system" ? systemPreference : theme;

  const appendText = (text: string) => {
    setSegments((s) => {
      const last = s[s.length - 1];
      if (last?.type === "text") {
        return [
          ...s.slice(0, -1),
          { type: "text", content: last.content + text },
        ];
      }
      return [...s, { type: "text", content: text }];
    });
  };

  const appendTool = (tool: Tool) => {
    setSegments((s) => {
      const last = s[s.length - 1];
      if (last?.type === "tools") {
        return [
          ...s.slice(0, -1),
          { type: "tools", tools: [...last.tools, tool] },
        ];
      }
      return [...s, { type: "tools", tools: [tool] }];
    });
  };

  const markToolError = (id: string, error: string) => {
    setSegments((s) =>
      s.map((seg) => {
        if (seg.type !== "tools") return seg;
        return {
          ...seg,
          tools: seg.tools.map((t) => (t.id === id ? { ...t, error } : t)),
        };
      }),
    );
  };

  const startThinking = () => {
    setSegments((s) => [...s, { type: "thinking", content: "", done: false }]);
  };

  const appendThinking = (text: string) => {
    setSegments((s) => {
      const last = s[s.length - 1];
      if (last?.type === "thinking") {
        return [
          ...s.slice(0, -1),
          { type: "thinking", content: last.content + text, done: false },
        ];
      }
      return s;
    });
  };

  const stopThinking = () => {
    setSegments((s) => {
      const last = s[s.length - 1];
      if (last?.type === "thinking") {
        return [...s.slice(0, -1), { ...last, done: true }];
      }
      return s;
    });
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setSegments([]);
    setSources([]);
    setHoveredFootnote(null);

    let sourceIndex = 1;
    const sourceList: { index: number; citation: Citation }[] = [];
    const chartedUrls = new Set<string>();
    const toolDataLocal = new Map<string, { date: string; value: string }[]>();
    const toolMetadataLocal = new Map<string, string>(); // units by source URL
    const pendingCharts: {
      url: string;
      title: string;
      units: string;
      data: { date: string; value: string }[];
    }[] = [];

    // Helper to flush pending charts as segments
    const flushPendingCharts = () => {
      for (const chart of pendingCharts) {
        setSegments((s) => [...s, { type: "chart", ...chart }]);
      }
      pendingCharts.length = 0;
    };

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
      });

      if (!res.body) {
        setSegments([
          { type: "text", content: "Error: Empty response from server" },
        ]);
        setLoading(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // Persist across chunks: `event:` and `data:` lines can land in different chunks.
      let currentEvent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7);
          } else if (line.startsWith("data: ") && currentEvent) {
            try {
              const data = JSON.parse(line.slice(6));
              if (currentEvent === "thinking_start") {
                startThinking();
              } else if (currentEvent === "thinking") {
                appendThinking(data.text);
              } else if (currentEvent === "thinking_stop") {
                stopThinking();
              } else if (currentEvent === "text") {
                const text = data.text as string;
                // Check for paragraph breaks - if we have pending charts, insert them
                if (pendingCharts.length > 0 && text.includes("\n\n")) {
                  const idx = text.indexOf("\n\n");
                  // Append text before the break
                  if (idx > 0) appendText(text.slice(0, idx));
                  // Flush charts at the paragraph break
                  flushPendingCharts();
                  // Append the rest (including the newlines)
                  appendText(text.slice(idx));
                } else {
                  appendText(text);
                }
              } else if (currentEvent === "block_citations") {
                const indices: number[] = [];
                const citations: Citation[] = data.citations ?? [];
                const chartsToAdd: {
                  url: string;
                  title: string;
                  units: string;
                  data: { date: string; value: string }[];
                }[] = [];
                for (const c of citations) {
                  const index = sourceIndex++;
                  sourceList.push({ index, citation: c });
                  indices.push(index);
                  // Check if this citation has chart data we haven't shown yet
                  const url = c.source || c.url || "";
                  if (url && !chartedUrls.has(url)) {
                    const observations = toolDataLocal.get(url);
                    if (observations) {
                      chartedUrls.add(url);
                      chartsToAdd.push({
                        url,
                        title: c.title || c.document_title || url,
                        units: toolMetadataLocal.get(url) || "",
                        data: observations,
                      });
                    }
                  }
                }
                if (indices.length > 0) {
                  const markers = indices
                    .map((i) => `<sup><a href="#source-${i}">[${i}]</a></sup>`)
                    .join("");
                  appendText(markers);
                }
                // Queue charts to insert at next paragraph break
                pendingCharts.push(...chartsToAdd);
                setSources([...sourceList]);
              } else if (currentEvent === "tool_use") {
                appendTool({
                  id: data.id,
                  name: data.name,
                  input: data.input || {},
                });
              } else if (currentEvent === "tool_error") {
                markToolError(data.id, data.error);
              } else if (currentEvent === "tool_metadata") {
                // Store metadata (units) by source URL
                const { source, units } = data;
                if (source && units) {
                  toolMetadataLocal.set(source, units);
                }
              } else if (currentEvent === "tool_result") {
                // Extract observations from tool results
                const content = data.content;
                if (Array.isArray(content)) {
                  for (const item of content) {
                    if (item.type === "search_result" && item.source) {
                      const textChunks =
                        item.content?.filter(
                          (c: { type: string }) => c.type === "text",
                        ) || [];
                      const observations: { date: string; value: string }[] =
                        [];
                      for (const chunk of textChunks) {
                        const text = chunk.text?.trim();
                        if (text) {
                          const dateMatch = text.match(
                            /^(\d{4}-\d{2}-\d{2}):\s*(.+)$/,
                          );
                          if (dateMatch) {
                            observations.push({
                              date: dateMatch[1],
                              value: dateMatch[2],
                            });
                          }
                        }
                      }
                      if (observations.length > 0) {
                        toolDataLocal.set(item.source, observations);
                      }
                    }
                  }
                }
              } else if (currentEvent === "error") {
                const isClaude = data.source === "claude";
                const prefix = isClaude ? "Claude API error" : "Error";
                const suffix = isClaude
                  ? " If this persists, check the [Anthropic Status Page](https://status.anthropic.com) for outages."
                  : "";
                appendText(`\n\n${prefix}: ${data.message}${suffix}`);
              }
            } catch (e) {
              console.warn("Failed to parse SSE data:", e);
            }
            currentEvent = "";
          }
        }
      }
    } catch (error) {
      setSegments([
        {
          type: "text",
          content: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
        },
      ]);
    }
    // Flush any remaining charts at end of stream
    flushPendingCharts();
    setLoading(false);
  }

  const hasConversation = loading || segments.length > 0;

  // Get highlighted dates for a specific source URL based on hovered footnote
  const getHighlightedDatesForSource = (sourceUrl: string): string[] => {
    if (hoveredFootnote === null) return [];
    const source = sources.find((s) => s.index === hoveredFootnote);
    if (!source) return [];
    const citationUrl = source.citation.source || source.citation.url || "";
    if (citationUrl !== sourceUrl) return [];
    return source.citation.citedDates || [];
  };

  // Helper to extract text content from React children
  const getTextContent = (node: React.ReactNode): string => {
    if (typeof node === "string") return node;
    if (typeof node === "number") return String(node);
    if (!node) return "";
    if (Array.isArray(node)) return node.map(getTextContent).join("");
    if (React.isValidElement(node)) {
      const props = node.props as { children?: React.ReactNode };
      return getTextContent(props.children);
    }
    return "";
  };

  // Custom sup component for footnotes with hover highlighting
  const FootnoteSup: Components["sup"] = ({ children }) => {
    // Extract footnote indices from children like "[1][2]" or "[1]"
    const text = getTextContent(children);
    const matches = text.match(/\[(\d+)\]/g);
    if (!matches) {
      return <sup>{children}</sup>;
    }

    const indices = matches.map((m) => parseInt(m.slice(1, -1)));
    const firstIndex = indices[0];

    return (
      <sup
        className="footnote-sup"
        onMouseEnter={() =>
          firstIndex !== undefined && setHoveredFootnote(firstIndex)
        }
        onMouseLeave={() => setHoveredFootnote(null)}
      >
        {children}
      </sup>
    );
  };

  const suggestedQuestions = [
    "How is the economy?",
    "How tight is the labor market?",
    "Is inflation coming down?",
    "Are we in a recession?",
  ];

  const handleSuggestionClick = (question: string) => {
    setQuery(question);
    setTimeout(() => {
      const form = document.querySelector("form");
      form?.requestSubmit();
    }, 0);
  };

  return (
    <Theme appearance={resolvedTheme} accentColor="blue" radius="none">
      <div className="fixed top-4 right-4">
        <DropdownMenu.Root modal={false}>
          <DropdownMenu.Trigger>
            {/* Icons from Lucide: sun, moon, monitor (1.25 stroke width) */}
            <IconButton variant="ghost" color="gray" aria-label="Theme">
              {theme === "system" ? (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="20" height="14" x="2" y="3" rx="2" />
                  <line x1="8" x2="16" y1="21" y2="21" />
                  <line x1="12" x2="12" y1="17" y2="21" />
                </svg>
              ) : theme === "light" ? (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2" />
                  <path d="M12 20v2" />
                  <path d="m4.93 4.93 1.41 1.41" />
                  <path d="m17.66 17.66 1.41 1.41" />
                  <path d="M2 12h2" />
                  <path d="M20 12h2" />
                  <path d="m6.34 17.66-1.41 1.41" />
                  <path d="m19.07 4.93-1.41 1.41" />
                </svg>
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401" />
                </svg>
              )}
            </IconButton>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            <DropdownMenu.Item onSelect={() => setTheme("system")}>
              System
            </DropdownMenu.Item>
            <DropdownMenu.Item onSelect={() => setTheme("light")}>
              Light
            </DropdownMenu.Item>
            <DropdownMenu.Item onSelect={() => setTheme("dark")}>
              Dark
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>
      <div
        className={`max-w-3xl mx-auto p-6 ${!hasConversation ? "min-h-screen flex flex-col" : ""}`}
      >
        <div
          className={
            !hasConversation ? "flex-1 flex flex-col justify-center" : ""
          }
        >
          <h1 className="text-2xl font-bold mb-2 [font-variant:small-caps]">
            EconStats
          </h1>
          <p className="text-secondary mb-4">US economic data with context</p>

          <form onSubmit={handleSubmit}>
            <Flex gap="2">
              <TextField.Root
                placeholder="Ask about the economy, inflation, jobs..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="flex-1"
                size="3"
              />
              <Button type="submit" disabled={loading} size="3">
                {loading ? "Thinking..." : "Ask"}
              </Button>
            </Flex>
          </form>

          {!hasConversation && (
            <Flex gap="2" mt="4" wrap="wrap">
              {suggestedQuestions.map((question) => (
                <Button
                  key={question}
                  variant="soft"
                  color="gray"
                  size="2"
                  onClick={() => handleSuggestionClick(question)}
                >
                  {question}
                </Button>
              ))}
            </Flex>
          )}
        </div>

        {!hasConversation && (
          <div className="text-secondary text-sm py-6">
            <p className="italic">
              Ask questions in plain English. We'll leverage AI to answer with
              official data and explanations.
              <span className="text-faint mx-2">·</span>
              <button
                onClick={() => setAboutOpen(true)}
                className="link-accent cursor-pointer not-italic"
              >
                Learn more
              </button>
            </p>
          </div>
        )}

        {hasConversation && (
          <Card mt="4" size="2">
            <div className="prose prose-sm max-w-none">
              {segments.map((seg, i) => {
                const isLastSegment = i === segments.length - 1;
                const isRunning =
                  loading && isLastSegment && seg.type === "tools";
                if (seg.type === "thinking") {
                  return (
                    <ThinkingBlock
                      key={i}
                      content={seg.content}
                      done={seg.done}
                    />
                  );
                }
                if (seg.type === "tools") {
                  return (
                    <div
                      key={i}
                      className="my-3 py-2 px-3 flex flex-wrap items-center gap-2"
                      style={{ backgroundColor: "var(--gray-a2)" }}
                    >
                      <span className="text-xs text-tertiary">Tools:</span>
                      {seg.tools.map((tool, j) => (
                        <ToolChip key={j} tool={tool} isRunning={isRunning} />
                      ))}
                    </div>
                  );
                }
                if (seg.type === "chart") {
                  return (
                    <div key={i} className="my-6">
                      <a
                        href={seg.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-bold text-secondary block pb-2 no-underline hover:underline"
                        title={seg.title}
                      >
                        {seg.title}
                      </a>
                      <div className="max-w-md">
                        <SourceChart
                          data={seg.data}
                          highlightedDates={getHighlightedDatesForSource(
                            seg.url,
                          )}
                          title={seg.title}
                          units={seg.units}
                        />
                      </div>
                    </div>
                  );
                }
                return (
                  <Markdown
                    key={i}
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={{ sup: FootnoteSup }}
                  >
                    {seg.content}
                  </Markdown>
                );
              })}
              {loading && <TypingIndicator />}
            </div>
            {(() => {
              const nonChartSources = sources.filter(({ citation }) => {
                const url = citation.source || citation.url || "";
                return !url.includes("fred.stlouisfed.org") && !url.includes("data.bls.gov");
              });
              if (nonChartSources.length === 0) return null;
              return (
                <div className="mt-6 pt-4 border-t border-subtle text-sm">
                  <div className="font-semibold mb-2">Sources</div>
                  {nonChartSources.map(({ index, citation }) => {
                    const url = citation.source || citation.url || "";
                    const title =
                      citation.title ||
                      citation.document_title ||
                      `Source ${index}`;
                    const citedText = citation.cited_text?.trim();

                    return (
                      <div key={index} id={`source-${index}`} className="py-1">
                        {url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="link-muted"
                          >
                            [{index}] {title}
                          </a>
                        ) : (
                          <span className="text-tertiary">
                            [{index}] {title}
                          </span>
                        )}
                        {citedText && (
                          <div
                            className="ml-4 mt-1 p-2 text-xs text-muted whitespace-pre-line w-fit"
                            style={{ backgroundColor: "var(--gray-a2)" }}
                          >
                            {citedText}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </Card>
        )}
      </div>

      <Dialog.Root open={aboutOpen} onOpenChange={setAboutOpen}>
        <Dialog.Content style={{ position: "relative", padding: "var(--space-6)" }}>
          <div style={{ position: "absolute", top: 16, right: 16 }}>
            <Dialog.Close>
              <IconButton variant="ghost" color="gray" aria-label="Close">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </IconButton>
            </Dialog.Close>
          </div>

          <Dialog.Title size="6" weight="bold">
            About{" "}
            <span className="[font-variant:small-caps]">EconStats</span>
          </Dialog.Title>

          <div className="prose prose-sm max-w-none mt-4">
            <p>
              Government economic data is free, but out of reach for too many
              people. The Federal Reserve, Bureau of Labor Statistics, Bureau of
              Economic Analysis, and Census Bureau publish thousands of data
              series every month — unemployment, inflation, GDP, wages, housing,
              trade, and more. But finding the right series, interpreting it
              correctly, and putting it in context requires expertise most people
              don't have.
            </p>
            <p>
              <span className="[font-variant:small-caps]">EconStats</span> uses AI to change that. Ask a question in plain
              English and get a real answer drawn directly from official
              government data.
            </p>
            <p>
              We're a California non-profit and are actively looking for
              institutional partners.
            </p>

            <h3>What makes <span className="[font-variant:small-caps]">EconStats</span> different from general-purpose LLMs</h3>
            <p>
              <span className="[font-variant:small-caps]">EconStats</span> pulls data directly from FRED — the Federal Reserve Bank
              of St. Louis's database of over 800,000 economic data series. When
              you ask a question, <span className="[font-variant:small-caps]">EconStats</span> selects the most appropriate data
              series, retrieves the latest values, and builds an answer grounded
              in that data. Every number is cited and traceable. In our internal
              evaluations, it significantly outperforms off-the-shelf
              general-purpose LLMs on economic questions, especially in using
              up-to-date data, and picking the right data to answer a user's
              question.
            </p>

            <h3>Who we are</h3>
            <p>
              <em>Josh Waldman</em> is a former economic policy staffer at the White
              House, now a graduate student at Stanford.
            </p>
            <p>
              <em>Jules Becker</em> is a software engineer and researcher.
            </p>
            <p>
              <em>Steven Berit</em> is an economic policy professional at MIT,
              formerly at the White House.
            </p>
          </div>

        </Dialog.Content>
      </Dialog.Root>
    </Theme>
  );
}
